import React, { useEffect, useMemo, useState } from "react";
import PageTopBar from "../../../../components/common/PageTopBar";
import { toolbarFieldsSx } from "../../../../components/common/toolbarStyles";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
} from "@mui/material";
import { useClassSubjects } from "../../hooks/useClassSubjects";
import { ClassSubjectsGrade, ClassSubjectsSection } from "../../types";
import { useNotify } from "../../../../context/NotificationContext";

const ALL = "all";

/**
 * Which subjects each class (grade + section) has. Ticks are saved together with "Save changes";
 * rows with unsaved changes are highlighted. All data comes from one call (GET /class-subjects).
 */
/** `tabs`: when shown inside Academics → Setup, the tabs replace the title in the top bar. */
const ClassSubjectsUI: React.FC<{ tabs?: React.ReactNode }> = ({ tabs }) => {
  const notify = useNotify();
  const { subjects, grades, draft, changedIds, loading, saving, error, actions } = useClassSubjects();
  const [gradeFilter, setGradeFilter] = useState<string>(ALL);

  // warn before closing/reloading the tab with unsaved changes
  useEffect(() => {
    if (changedIds.length === 0) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [changedIds.length]);

  const visibleGrades = useMemo(
    () => grades.filter((grade) => gradeFilter === ALL || String(grade.id) === gradeFilter),
    [grades, gradeFilter]
  );

  const handleSave = async () => {
    const failed = await actions.saveAll();
    if (failed === 0) notify("Class subjects saved.", "success");
  };

  const columnCount = subjects.length + 2;

  const renderClassRow = (grade: ClassSubjectsGrade, section: ClassSubjectsSection, index: number) => {
    const id = section.gradeSectionId;
    const changed = changedIds.includes(id);
    return (
      <TableRow
        key={id}
        hover
        sx={{ bgcolor: (theme) => (changed ? alpha(theme.palette.warning.light, 0.15) : undefined) }}
      >
        <TableCell sx={{ py: 0.5, pl: 4 }}>
          {section.name}
          {changed && (
            <Typography component="span" variant="caption" color="warning.main" ml={1}>
              unsaved
            </Typography>
          )}
        </TableCell>
        {subjects.map((subject) => (
          <TableCell key={subject.id} align="center" sx={{ py: 0 }}>
            <Checkbox
              size="small"
              checked={(draft[id] ?? []).includes(subject.code)}
              onChange={() => actions.toggle(id, subject.code)}
              disabled={saving}
              inputProps={{ "aria-label": `${grade.name} ${section.name} ${subject.name}` }}
            />
          </TableCell>
        ))}
        <TableCell align="right" sx={{ py: 0.5, pr: 2 }}>
          {index === 0 && grade.sections.length > 1 && (
            <Button size="small" onClick={() => actions.copyToGrade(id, grade.id)} disabled={saving}>
              Copy {section.name} → all sections
            </Button>
          )}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Box>
      <PageTopBar
        left={tabs ?? "Class Subjects"}
        right={
        <FormControl size="small" sx={{ ...toolbarFieldsSx, minWidth: 160 }}>
          <InputLabel id="class-subjects-grade">Grade</InputLabel>
          <Select
            labelId="class-subjects-grade"
            label="Grade"
            value={gradeFilter}
            onChange={(event) => setGradeFilter(event.target.value)}
          >
            <MenuItem value={ALL}>All</MenuItem>
            {grades.map((grade) => (
              <MenuItem key={grade.id} value={String(grade.id)}>
                {grade.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* The table scrolls inside itself (height capped to the screen) so the stickyHeader row of
          subject names stays visible while scrolling through classes, and Save stays on screen. */}
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ maxHeight: { xs: "max(320px, calc(100dvh - 330px))", md: "max(360px, calc(100dvh - 250px))" } }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, py: 1, bgcolor: "grey.50" }}>Class</TableCell>
              {subjects.map((subject) => (
                <TableCell key={subject.id} align="center" sx={{ fontWeight: 600, py: 1, bgcolor: "grey.50" }}>
                  {subject.name}
                </TableCell>
              ))}
              <TableCell sx={{ bgcolor: "grey.50" }} />
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columnCount} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : subjects.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columnCount} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    No subjects yet. Add subjects first.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              visibleGrades.map((grade) => (
                <React.Fragment key={grade.id}>
                  <TableRow>
                    <TableCell colSpan={columnCount} sx={{ fontWeight: 600, py: 0.75, bgcolor: "grey.100" }}>
                      {grade.name}
                    </TableCell>
                  </TableRow>
                  {grade.sections.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={columnCount} sx={{ py: 0.75, pl: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No sections. Add sections in Grades first.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    grade.sections.map((section, index) => renderClassRow(grade, section, index))
                  )}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
        <Button onClick={actions.discard} disabled={saving || changedIds.length === 0}>
          Discard
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || changedIds.length === 0}>
          {saving ? <CircularProgress size={18} /> : `Save changes${changedIds.length ? ` (${changedIds.length})` : ""}`}
        </Button>
      </Box>
    </Box>
  );
};

export default ClassSubjectsUI;
